import * as THREE from "three";

// Shared low-poly humanoid pose writer for InstancedMesh crowds.
// Bodies are assembled from a small set of body-part instanced meshes and
// posed procedurally (a lightweight "skeletal" walk cycle) so 150 figures
// stay a handful of draw calls while still animating limbs.

// Unit humanoid (feet at y=0, height ~0.94). Geometry for each part is
// pre-translated so the INSTANCE origin sits at the joint pivot, letting us
// rotate a limb about its hip/shoulder just by setting rotation on the part.
export const H = {
  hipY: 0.5,
  legLen: 0.5,
  torsoH: 0.35,
  shoulderY: 0.82,
  armLen: 0.34,
  headY: 0.94,
  headR: 0.1,
  legX: 0.12,
  armX: 0.2,
};

export function makeGeometries({ robe = false, legLen = H.legLen } = {}) {
  const g = {};
  // legs — pivot at hip (top)
  g.leg = new THREE.CylinderGeometry(0.05, 0.045, legLen, 6);
  g.leg.translate(0, -legLen / 2, 0);
  // torso — pivot at hip (bottom). Robe = longer, flared.
  const th = robe ? 0.62 : H.torsoH;
  g.torso = new THREE.CylinderGeometry(robe ? 0.1 : 0.11, robe ? 0.2 : 0.13, th, robe ? 10 : 7);
  g.torso.translate(0, th / 2, 0);
  // arms — pivot at shoulder (top)
  g.arm = new THREE.CylinderGeometry(0.04, 0.035, H.armLen, 6);
  g.arm.translate(0, -H.armLen / 2, 0);
  // head
  g.head = new THREE.SphereGeometry(H.headR, 12, 10);
  // accessory placeholders
  g.pack = new THREE.BoxGeometry(0.18, 0.22, 0.1); // backpack
  g.cap = new THREE.BoxGeometry(0.26, 0.03, 0.26); // mortarboard
  return g;
}

// reusable temporaries
const _root = new THREE.Object3D();
const _p = new THREE.Object3D();
const _m = new THREE.Matrix4();

export function setRoot(x, y, z, facing, sx, sy, sz) {
  _root.position.set(x, y, z);
  _root.rotation.set(0, facing, 0);
  _root.scale.set(sx, sy, sz);
  _root.updateMatrix();
}

export function writePart(mesh, i, px, py, pz, rx = 0, ry = 0, rz = 0) {
  if (!mesh) return;
  _p.position.set(px, py, pz);
  _p.rotation.set(rx, ry, rz);
  _p.scale.set(1, 1, 1);
  _p.updateMatrix();
  _m.multiplyMatrices(_root.matrix, _p.matrix);
  mesh.setMatrixAt(i, _m);
}

// Pose a full humanoid given a walk cycle. Parts is a map of instancedMesh refs.
export function poseHumanoid(parts, i, pose) {
  const {
    legSwing = 0,
    armSwing = 0,
    lean = 0,
    sway = 0,
    robe = false,
    legLen = H.legLen,
  } = pose;
  // legs swing opposite each other
  writePart(parts.legL, i, -H.legX, H.hipY, 0, legSwing, 0, 0);
  writePart(parts.legR, i, H.legX, H.hipY, 0, -legSwing, 0, 0);
  // torso leans forward with hustle, sways side to side
  writePart(parts.torso, i, 0, H.hipY, 0, lean, 0, sway);
  // arms swing opposite to legs
  writePart(parts.armL, i, -H.armX, H.shoulderY, 0, -armSwing, 0, 0.08);
  writePart(parts.armR, i, H.armX, H.shoulderY, 0, armSwing, 0, -0.08);
  // head tracks the lean
  writePart(parts.head, i, 0, H.headY + lean * 0.05, lean * 0.1, lean * 0.6, 0, sway * 0.5);
  if (parts.pack) writePart(parts.pack, i, 0, H.hipY + 0.28, -0.14, lean, 0, 0);
  if (parts.cap) writePart(parts.cap, i, 0, H.headY + H.headR + 0.01, 0, 0, 0, 0);
}

export function flush(parts) {
  Object.values(parts).forEach((m) => {
    if (m) m.instanceMatrix.needsUpdate = true;
  });
}
